// Shared plain-text helpers for feed-sourced copy (titles, excerpts).
// Feed content routinely contains HTML entities and stray markup; we always
// render it as plain text, never as HTML.

const NAMED: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
  hellip: "…",
  mdash: "—",
  ndash: "–",
  lsquo: "\u2018",
  rsquo: "\u2019",
  ldquo: "\u201C",
  rdquo: "\u201D",
  eacute: "é",
  uuml: "ü",
  deg: "°",
  trade: "™",
  copy: "©",
  reg: "®",
};

/** Decode numeric (&#8217; &#x27;) and common named HTML entities. */
export function decodeEntities(input: string): string {
  if (!input) return "";
  let out = input.replace(/&#(\d+);/g, (_, d) => safeFromCodePoint(parseInt(d, 10)));
  out = out.replace(/&#x([0-9a-f]+);/gi, (_, h) => safeFromCodePoint(parseInt(h, 16)));
  out = out.replace(/&([a-z][a-z0-9]*);/gi, (m, name) => {
    const v = NAMED[String(name).toLowerCase()];
    return v ?? m;
  });
  return out;
}

function safeFromCodePoint(code: number): string {
  if (!Number.isFinite(code) || code < 0 || code > 0x10ffff) return "";
  try {
    return String.fromCodePoint(code);
  } catch {
    return "";
  }
}

/** Remove any HTML tags / CDATA wrappers, leaving text only. */
export function stripTags(input: string): string {
  if (!input) return "";
  return input
    .replace(/<!\[CDATA\[([\s\S]*?)\]\]>/g, "$1")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<[^>]*>/g, " ");
}

/** Strip markup, decode entities, collapse whitespace. Safe for text nodes. */
export function toPlainText(input: string | null | undefined): string {
  if (!input) return "";
  // Decode twice: feeds are frequently double-encoded (&amp;#8217;).
  let out = decodeEntities(stripTags(input));
  out = stripTags(decodeEntities(out));
  return out.replace(/\s+/g, " ").trim();
}

/** Plain-text excerpt for cards/previews: strips markup, decodes entities, truncates on a word boundary. */
export function toExcerpt(input: string | null | undefined, maxChars = 280): string {
  const text = toPlainText(input);
  if (text.length <= maxChars) return text;
  const cut = text.slice(0, maxChars);
  const lastSpace = cut.lastIndexOf(" ");
  return `${(lastSpace > maxChars * 0.6 ? cut.slice(0, lastSpace) : cut).replace(/[.,;:!?-]+$/, "")}…`;
}
